import { FormikHelpers } from "formik";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "react-i18next";
import { IFormProps } from "../../components/formik/IFormProps";
import { IDisclosure } from "../../models/IDisclosure";
import { meta } from "../../utils";
import { FormFooter } from "./FormFooter";
import { FormHeader } from "./FormHeader";
import { onScoreRender } from "./ScoreIndicator";
import { onRenderSeparator } from "./Separator";
import { validation } from "./validation";
import { CvssService } from "./CvssService";


export const useDisclosure = () => {
    const { t } = useTranslation("signalement");
    const recaptcha = useGoogleReCaptcha();
    const productTypes = [
        t("Labels.ChoicePlaceholder", "Faites un choix"),
        t("ProductTypes.CrossSiteScripting", "Cross-site Scripting (XSS)"),
        t("ProductTypes.ImproperAccessControl", "Improper Access Control"),
        t("ProductTypes.Information Disclosure", "Information Disclosure"),
        t("ProductTypes.ServerSideRequestForgery", "Server-Side Request Forgery (SSRF)"),
        t("ProductTypes.InsecureDirectObjectReference", "Insecure Direct Object Reference (IDOR)"),
        t("ProductTypes.PrivilegeEscalation", "Privilege Escalation"),
        t("ProductTypes.RemoteCodeExecution", "Remote Code Execution (RCE)"),
        t("ProductTypes.SQLInjection", "SQL Injection"),
        t("ProductTypes.ImproperAuthentication", "Improper Authentication"),
        t("ProductTypes.CodeInjection", "Code Injection"),
        t("ProductTypes.CrossSiteRequestForgery", "Cross-Site Request Forgery (CSRF)"),
        t("ProductTypes.Other", "Other")
    ].map((x, i) => ({
        key: i,
        text: x
    }));

    const simpleRatings = [
        t("SimpleRating.None", "None"),
        t("SimpleRating.Low", "Low"),
        t("SimpleRating.Medium", "Medium"),
        t("SimpleRating.High", "High"),
        t("SimpleRating.Critical", "Critical")
    ].map((x, i) => ({
        key: i,
        text: x
    }));


    const scopes = [
        t("Scopes.Unchanged", "Unchanged"),
        t("Scopes.Changed", "Changed"),
    ].map((x, i) => ({
        key: i,
        text: x
    }));

    const attackVectors = [
        t("AttackVectors.Network", "Network"),
        t("AttackVectors.Adjacent", "Adjacent"),
        t("AttackVectors.Local", "Local"),
        t("AttackVectors.Physical", "Physical")
    ].map((x, i) => ({
        key: i,
        text: x
    }));

    const complexities = [
        t("DefaultLevel.Low", "Low"),
        t("DefaultLevel.High", "High")
    ].map((x, i) => ({
        key: i,
        text: x
    }));

    const privilegesRequired = [
        t("DefaultLevel.None", "None"),
        t("DefaultLevel.Low", "Low"),
        t("DefaultLevel.High", "High")
    ].map((x, i) => ({
        key: i,
        text: x
    }));

    const integrities = [
        t("DefaultLevel.None", "None"),
        t("DefaultLevel.Low", "Low"),
        t("DefaultLevel.High", "High")
    ].map((x, i) => ({
        key: i,
        text: x
    }));

    const confidentialities = [
        t("DefaultLevel.None", "None"),
        t("DefaultLevel.Low", "Low"),
        t("DefaultLevel.High", "High")
    ].map((x, i) => ({
        key: i,
        text: x
    }));

    const userInteractions = [
        t("DefaultLevel.None", "None"),
        t("Required", "Required")
    ].map((x, i) => ({
        key: i,
        text: x
    }));
    const availabilities = [
        t("DefaultLevel.None", "None"),
        t("DefaultLevel.Low", "Low"),
        t("DefaultLevel.High", "High")
    ].map((x, i) => ({
        key: i,
        text: x
    }));

    const onSubmit = (values: IDisclosure, formikHelpers: FormikHelpers<IDisclosure>) => {
        recaptcha.executeRecaptcha?.().then(x => {
            values.recaptcha = x;
            fetch(`${meta("WWW-psv-api")}`, {
                method: "POST",
                body: JSON.stringify({
                    ...values,
                    score: CvssService.calculate(t, values)?.baseScore ?? 0
                }),
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                },
            })
                .then(x => x.status > 299 ? { id: undefined } : x.json())
                .then(x => {
                    if (x?.id) {
                        const message = t("Labels.Success", "Your report has been successfully submitted.");
                        const id = t("Labels.SuccessIdentity", x);
                        formikHelpers.setSubmitting(false);
                        formikHelpers.resetForm();
                        (document.getElementById("description") as any)?.ck?.setData("");
                        (document.getElementById("impacts") as any)?.ck?.setData("");
                        (document.getElementById("recommandations") as any)?.ck?.setData("");
                        formikHelpers.setStatus({
                            success: true,
                            message,
                            id
                        });
                    }
                    else {
                        const message = t("Labels.Failure", "Your report cannot be submitted at the moment. Sorry for the inconvenience");
                        formikHelpers.setSubmitting(false);
                        formikHelpers.setStatus({
                            success: false,
                            message,
                            id: ""
                        });
                    }
                    window.scrollTo(0, 0);
                }).catch(x => {
                    console.log(x);
                    const message = t("Labels.Failure", "Your report cannot be submitted at the moment. Sorry for the inconvenience");
                    formikHelpers.setSubmitting(false);
                    formikHelpers.setStatus({
                        success: false,
                        message,
                        id: ""
                    });
                    window.scrollTo(0, 0);
                });
        });
    };

    const formProps: IFormProps = {
        initialValues: {
            product: "",
            productType: 0,
            description: "",
            impacts: "",
            recommandations: "",
            eula: false,
            recaptcha: undefined
        },
        validationSchema: validation(t),
        schema: {
            form: {
                renderer: "grouped",
                onRenderLayoutHeader: FormHeader(t),
                onRenderLayoutFooter: FormFooter,
            },
            fields: [
                {
                    key: "product",
                    name: "product",
                    label: t("Labels.Product", "Product or website concerned"),
                    group: t("Groups.Vulnerability", "Vulnerability"),
                    type: "text",
                    maxLength: 255,
                    required: true,
                },
                {
                    key: "productType",
                    name: "productType",
                    label: t("Labels.Type", "Type"),
                    group: t("Groups.Vulnerability", "Vulnerability"),
                    type: "option",
                    options: productTypes,
                    required: true,
                },
                {
                    key: "description",
                    name: "description",
                    label: t("Labels.Description", "Description"),
                    group: t("Groups.ProofOfconcept", "Proof of concept"),
                    type: "text",
                    multiline: "true",
                    rows: 7,
                    maxLength: 4500000,
                    className: "editor",
                    required: true,
                    gridClassName: "",
                    helpText: t("HelpTexts.Description", "Décrivez comment reproduire votre preuve de concept.") ?? ""
                },
                {
                    key: "impacts",
                    name: "impacts",
                    label: t("Labels.Impacts", "Impacts"),
                    group: t("Groups.ProofOfconcept", "Proof of concept"),
                    type: "text",
                    multiline: "true",
                    className: "editor",
                    rows: 5,
                    maxLength: 4500000,
                    gridClassName: "",
                    helpText: t("HelpTexts.Impacts", "Décrivez les possibles impacts de cette vulnérabilité.") ?? ""
                },
                {
                    key: "recommandations",
                    name: "recommandations",
                    label: t("Labels.Recommandations", "Recommandations"),
                    group: t("Groups.ProofOfconcept", "Proof of concept"),
                    type: "text",
                    multiline: "true",
                    className: "editor",
                    rows: 6,
                    maxLength: 4500000,
                    gridClassName: "",
                    helpText: t("HelpTexts.Recommandations", "Décrivez vos recommandations pour corriger ou mitiger cette vulnérabilité.") ?? ""
                },
                {
                    key: "score",
                    name: "score",
                    group: t("Groups.Cvss", "Système commun de notation des vulnérabilités (CVSS v3.1) - facultatif"),
                    type: "custom",
                    render: onScoreRender
                },
                {
                    key: "simpleRating",
                    name: "simpleRating",
                    label: t("Labels.SimpleRating", "Simple score"),
                    inline: true,
                    group: t("Groups.Cvss", "Système commun de notation des vulnérabilités (CVSS v3.1) - facultatif"),
                    type: "choice",
                    options: simpleRatings,
                },
                {
                    key: "separator",
                    name: "separator",
                    group: t("Groups.Cvss", "Système commun de notation des vulnérabilités (CVSS v3.1) - facultatif"),
                    type: "custom",
                    render: onRenderSeparator(t),
                },
                {
                    key: "attackVector",
                    name: "attackVector",
                    label: t("Labels.AttackVector", "Attack Vector"),
                    inline: true,
                    group: t("Groups.Cvss", "Système commun de notation des vulnérabilités (CVSS v3.1) - facultatif"),
                    type: "choice",
                    gridClassName: "col-lg-6",
                    options: attackVectors,
                },
                {
                    key: "scope",
                    name: "scope",
                    label: t("Labels.Scope", "Scope"),
                    inline: true,
                    group: t("Groups.Cvss", "Système commun de notation des vulnérabilités (CVSS v3.1) - facultatif"),
                    type: "choice",
                    gridClassName: "col-lg-6",
                    options: scopes,
                },
                {
                    key: "complexity",
                    name: "complexity",
                    label: t("Labels.AttackComplexity", "Attack complexity"),
                    inline: true,
                    group: t("Groups.Cvss", "Système commun de notation des vulnérabilités (CVSS v3.1) - facultatif"),
                    type: "choice",
                    gridClassName: "col-lg-6",
                    options: complexities,
                },
                {
                    key: "confidentiality",
                    name: "confidentiality",
                    label: t("Labels.Confidentiality", "Confidentiality"),
                    inline: true,
                    group: t("Groups.Cvss", "Système commun de notation des vulnérabilités (CVSS v3.1) - facultatif"),
                    type: "choice",
                    gridClassName: "col-lg-6",
                    options: confidentialities,
                },
                {
                    key: "privilegesRequired",
                    name: "privilegesRequired",
                    label: t("Labels.PrivilegesRequired", "Privileges Required"),
                    inline: true,
                    group: t("Groups.Cvss", "Système commun de notation des vulnérabilités (CVSS v3.1) - facultatif"),
                    type: "choice",
                    gridClassName: "col-lg-6",
                    options: privilegesRequired,
                },
                {
                    key: "integrity",
                    name: "integrity",
                    label: t("Labels.Integrity", "Integrity"),
                    inline: true,
                    group: t("Groups.Cvss", "Système commun de notation des vulnérabilités (CVSS v3.1) - facultatif"),
                    type: "choice",
                    gridClassName: "col-lg-6",
                    options: integrities,
                },
                {
                    key: "userInteraction",
                    name: "userInteraction",
                    label: t("Labels.UserInteraction", "User Interation"),
                    inline: true,
                    group: t("Groups.Cvss", "Système commun de notation des vulnérabilités (CVSS v3.1) - facultatif"),
                    type: "choice",
                    gridClassName: "col-lg-6",
                    options: userInteractions,
                },
                {
                    key: "availability",
                    name: "availability",
                    label: t("Labels.Availability", "Availability"),
                    inline: true,
                    group: t("Groups.Cvss", "Système commun de notation des vulnérabilités (CVSS v3.1) - facultatif"),
                    type: "choice",
                    gridClassName: "col-lg-6 cvss-col-lg-6",
                    options: availabilities,
                },
                {
                    key: "isContactAllowed",
                    name: "isContactAllowed",
                    label: t("Concent.Contact", "I authorize the Centre gouvernemental de cyberdéfense to contact me for more information on the vulnerability detected."),
                    inline: true,
                    group: t("Groups.Concent", "Concent"),
                    type: "boolean",
                },
                {
                    key: "isMentionAllowed",
                    name: "isMentionAllowed",
                    label: t("Concent.Public", "I authorize the Centre gouvernemental de cyberdéfense to add my name to the list of contributors to improving the security of digital services."),
                    inline: true,
                    group: t("Groups.Concent", "Concent"),
                    type: "boolean",
                },
                {
                    key: "eula",
                    name: "eula",
                    label: t("Concent.Eula", "I accept the conditions of the undertaking."),
                    nameLabel: t("Labels.Conditions", "Conditions"),
                    inline: true,
                    group: t("Groups.Concent", "Concent"),
                    type: "boolean",
                    required: true,
                },
                {
                    key: "name",
                    name: "name",
                    label: t("Labels.Name", "Nom ou pseudonyme"),
                    group: t("Groups.ReporterInformation", "Reporter information"),
                    type: "text",
                    maxLength: 255,
                },
                {
                    key: "reporterEmail",
                    name: "reporterEmail",
                    label: t("Labels.Email", "Email"),
                    group: t("Groups.ReporterInformation", "Reporter information"),
                    type: "email",
                    maxLength: 4096,
                },
                {
                    key: "pgpPublicKey",
                    name: "pgpPublicKey",
                    label: t("Labels.PGPKey", "PGP Public Key"),
                    group: t("Groups.ReporterInformation", "Reporter information"),
                    type: "text",
                    multiline: "true",
                    rows: 4,
                    maxLength: 14012,
                },
            ]
        },
        onSubmit: onSubmit
    }

    return { formProps };
}
